import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, Field as FormikField } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { registerUser } from "../../shared/_services/auth.services";
import { getChallengeToken, setChallengeToken } from "../../shared/_helpers/otpChallenge";
import { getCountryCode } from "../../shared/_services/common.services";
import { readCurrentUser } from "../../shared/_helpers/currentUser";
import { US_STATES } from "../../shared/_helpers/usStates";
import { Button, Field as UiField, Input, Select } from "../ui";
import { AuthCover, AuthHeading } from "./AuthCover";
import {
  ACCOUNT_CONFIRM_PASSWORD,
  ACCOUNT_CONTINUE,
  ACCOUNT_ELIGIBILITY,
  ACCOUNT_EMAIL,
  ACCOUNT_FIRST_NAME,
  ACCOUNT_H1,
  ACCOUNT_INTRO,
  ACCOUNT_LAST_NAME,
  ACCOUNT_MOBILE,
  ACCOUNT_NAME_REQUIRED,
  ACCOUNT_PASSWORD,
  ACCOUNT_PASSWORD_PLACEHOLDER,
  ACCOUNT_PRIVACY_LEAD,
  ACCOUNT_PRIVACY_LINK,
  ACCOUNT_SIDE_NEXT,
  ACCOUNT_SIDE_TITLE,
  ACCOUNT_STATE,
  ACCOUNT_VERIFIED_FIRST,
} from "../onboarding/onboardingCopy";

// The Privacy Notice at the moment of collection (owner decision 1, second
// round): a passive link, no tick, nothing recorded. This page is shown before
// sign-in and GET /onboarding/disclosures needs a session, so the link opens
// Astra's public privacy page — the notice the legacy checkbox linked to.
export const PRIVACY_NOTICE_URL = "https://www.astrawmg.com/privacy-policy/";

const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, "");
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  }
  return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const validationSchema = Yup.object().shape({
  firstName: Yup.string().trim().required(ACCOUNT_NAME_REQUIRED),
  lastName: Yup.string().trim().required(ACCOUNT_NAME_REQUIRED),
  email: Yup.string()
    .required("Email is required")
    .test("contains-at-and-dot", "Email must contain '@' and '.'", (value) => {
      if (!value) return false;
      return value.includes("@") && value.includes(".");
    })
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address"),
  mobile: Yup.string()
    .required("Phone number is required")
    .matches(
      /^(?!.*(\d)\1{2}-(\d)\2{2}-(\d)\3{3})\d{3}-\d{3}-\d{4}$/,
      "Invalid phone number format or repeated digits"
    ),
  password: Yup.string()
    .required("Password is required")
    .min(10, "Password must be at least 10 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[`~!@#$%^&*()_\-+=[\]{};:'",./<>?\\|]).{10,}$/,
      "Password should contain at least 1 number, 1 lowercase, 1 uppercase, 1 special character"
    ),
  // Today's must-match rule, kept by owner decision (2026-09-26). The field
  // is never sent to the server.
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm Password is required"),
  stateOfResidence: Yup.string().required("State of residence is required"),
});

// The pending account the client is coming back to from Verify ("Wrong
// number? Go back and change it."): still at signup_pending_verification, with
// the challenge token that proves this tab started it (R132). Anything else is
// a fresh signup.
function pendingAccount() {
  const user = readCurrentUser()?.user;
  const challengeToken = getChallengeToken();
  if (user?.onboardingStage === "signup_pending_verification" && challengeToken) {
    return { user, challengeToken };
  }
  return null;
}

const SignUp = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [countrycode, setCountryCodes] = useState([]);
  const location = useLocation();
  const [pending] = useState(pendingAccount);

  useEffect(() => {
    const fetchCountryCodes = async () => {
      const code = await getCountryCode();

      setCountryCodes(code?.data);
    };
    fetchCountryCodes();
  }, []);

  // Every field comes back except the two password boxes, typed again.
  const initialValues = {
    firstName: pending?.user.firstName ?? "",
    lastName: pending?.user.lastName ?? "",
    email: pending?.user.email ?? "",
    mobile: formatPhoneNumber(pending?.user.mobile ?? ""),
    password: "",
    confirmPassword: "",
    stateOfResidence: pending?.user.stateOfResidence ?? "",
    code: "+1",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const params = new URLSearchParams(location.search);
    const loginType = params.get('loginType');
    const { email, mobile, password, code, stateOfResidence } = values;
    const firstName = values.firstName.trim();
    const lastName = values.lastName.trim();

    let data = await registerUser({
      firstName,
      lastName,
      email,
      mobile,
      password,
      userType: "client",
      code,
      loginType,
      stateOfResidence,
      // R132: the re-submit that updates the pending account in place.
      ...(pending ? { challengeToken: pending.challengeToken } : {}),
    });
    if (data.status === 200) {
      let currentUser = { user: data?.data?.data, token: data?.data?.data?.token };
      delete currentUser.user.token;

      // Signup sends a verification code of its own and lands the new client on
      // /verify-code, so it also returns the challenge token that redeems it
      // (backend SEC-114). A flow credential, not part of the identity —
      // sessionStorage, and out of the persisted user.
      setChallengeToken(currentUser.user.challengeToken);
      delete currentUser.user.challengeToken;

      window.localStorage.setItem("currentUser", JSON.stringify(currentUser));
      if (currentUser?.user?.userType === 'client') {
        sessionStorage.setItem('verifyCodeFrom', 'signup');
        navigate('/verify-code', { state: { from: 'signup' } });
      }
    }
    else {
      enqueueSnackbar(data?.response?.data?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting, values, setFieldValue }) => {
        const nameError =
          (touched.firstName && errors.firstName) || (touched.lastName && errors.lastName);
        return (
          <Form noValidate>
            <AuthCover>
              <AuthHeading title={ACCOUNT_H1} lede={ACCOUNT_INTRO} />
              <div className="tw-grid tw-grid-cols-2 tw-gap-x-4">
                <div data-slot="onb-field">
                  <UiField label={ACCOUNT_FIRST_NAME}>
                    <FormikField as={Input} id="firstName" name="firstName" type="text" autoComplete="given-name" />
                  </UiField>
                </div>
                <div data-slot="onb-field">
                  <UiField label={ACCOUNT_LAST_NAME}>
                    <FormikField as={Input} id="lastName" name="lastName" type="text" autoComplete="family-name" />
                  </UiField>
                </div>
              </div>
              {nameError && (
                <p className="tw-mb-4 tw-text-sm tw-text-danger" role="alert">
                  {ACCOUNT_NAME_REQUIRED}
                </p>
              )}
              <div data-slot="onb-field">
                <UiField label={ACCOUNT_EMAIL} error={touched.email && errors.email}>
                  <FormikField as={Input} id="email" name="email" type="email" autoComplete="email" />
                </UiField>
              </div>
              <div data-slot="onb-field" className="tw-mb-4 tw-grid tw-gap-2">
                <label htmlFor="mobile" className="tw-text-sm tw-font-semibold tw-text-text">{ACCOUNT_MOBILE}</label>
                <div className="tw-flex tw-gap-2">
                  <FormikField as={Select} name="code" disabled wrapperClassName="tw-w-24 tw-flex-none">
                    {countrycode?.map((item) => (
                      <option key={item.code} value={item.code}>
                        ({item.code})
                      </option>
                    ))}
                  </FormikField>
                  <FormikField
                    as={Input}
                    id="mobile"
                    name="mobile"
                    type="tel"
                    autoComplete="tel-national"
                    invalid={Boolean(touched.mobile && errors.mobile)}
                    value={formatPhoneNumber(values.mobile)}
                    onChange={(e) => setFieldValue("mobile", formatPhoneNumber(e.target.value))}
                  />
                </div>
                {touched.mobile && errors.mobile && (
                  <p className="tw-text-sm tw-text-danger">{errors.mobile}</p>
                )}
              </div>
              <div data-slot="onb-field">
                <UiField label={ACCOUNT_PASSWORD} error={touched.password && errors.password}>
                  <FormikField
                    as={Input}
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    placeholder={ACCOUNT_PASSWORD_PLACEHOLDER}
                  />
                </UiField>
              </div>
              <div data-slot="onb-field">
                <UiField label={ACCOUNT_CONFIRM_PASSWORD} error={touched.confirmPassword && errors.confirmPassword}>
                  <FormikField
                    as={Input}
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                  />
                </UiField>
              </div>
              <div data-slot="onb-field">
                <UiField label={ACCOUNT_STATE} error={touched.stateOfResidence && errors.stateOfResidence}>
                  <FormikField as={Select} id="stateOfResidence" name="stateOfResidence">
                    <option value="" />
                    {US_STATES.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </FormikField>
                </UiField>
              </div>
              <p className="tw-mb-2 tw-text-xs tw-text-text-muted">{ACCOUNT_ELIGIBILITY}</p>
              <p className="tw-mb-6 tw-text-xs tw-text-text-muted">
                {ACCOUNT_PRIVACY_LEAD}
                <a
                  className="tw-font-bold tw-text-link"
                  href={PRIVACY_NOTICE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {ACCOUNT_PRIVACY_LINK}
                </a>
                .
              </p>
              <aside aria-label={ACCOUNT_SIDE_TITLE}>
                <h2 className="tw-mb-3 tw-text-lg tw-font-bold tw-text-text">{ACCOUNT_SIDE_TITLE}</h2>
                <Button type="submit" className="tw-w-full" disabled={isSubmitting}>
                  {ACCOUNT_CONTINUE}
                </Button>
                <p className="tw-mt-4 tw-text-xs tw-text-text-muted">{ACCOUNT_SIDE_NEXT}</p>
              </aside>
              <p className="tw-mt-3 tw-text-xs tw-text-text-muted">{ACCOUNT_VERIFIED_FIRST}</p>
            </AuthCover>
          </Form>
        );
      }}
    </Formik>
  );
};

export default SignUp;
